import Dinero from 'dinero.js';

/**
 * Money calculation utilities using Dinero.js
 * Provides safe, accurate money calculations avoiding floating-point errors
 *
 * All functions work with amounts in the currency's minor unit (cents for EUR)
 * to ensure precision throughout calculations.
 */

// Default currency is EUR
const DEFAULT_CURRENCY = 'EUR';

/**
 * Create a Dinero money object from euros
 * @param {number} amount - Amount in euros (e.g., 299.99)
 * @param {string} currency - Currency code (default: EUR)
 * @returns {Dinero} Dinero money object
 */
export const money = (amount, currency = DEFAULT_CURRENCY) => {
  return Dinero({
    amount: Math.round(amount * 100), // Convert to cents
    currency,
  });
};

/**
 * Convert Dinero object back to decimal amount
 * @param {Dinero} dineroObj - Dinero money object
 * @returns {number} Amount in euros (e.g., 299.99)
 */
export const toAmount = (dineroObj) => {
  return dineroObj.toUnit();
};

/**
 * Convert Dinero object to price object format used in the app
 * @param {Dinero} dineroObj - Dinero money object
 * @returns {Object} Price object with amount and currencyCode
 */
export const toPriceObject = (dineroObj) => {
  return {
    amount: dineroObj.toUnit(),
    currencyCode: dineroObj.getCurrency(),
  };
};

/**
 * Allocate a discount proportionally across products
 * This ensures the total always adds up correctly, distributing rounding errors fairly
 *
 * Uses the "Largest Remainder Method" to handle remainders:
 * - Calculate proportional discounts
 * - Floor all values
 * - Distribute leftover cents to products with largest remainders
 *
 * @param {Array} products - Array of products with price.amount
 * @param {number} totalDiscountAmount - Total discount in euros
 * @param {string} currency - Currency code (default: EUR)
 * @returns {Array} Products with allocated discount amounts
 *
 * @example
 * const products = [
 *   { id: 1, price: { amount: 299.99 } },
 *   { id: 2, price: { amount: 10.90 } }
 * ];
 * const result = allocateDiscount(products, 50.00);
 * // Returns products with exact discount allocation that sums to 50.00
 */
export const allocateDiscount = (products, totalDiscountAmount, currency = DEFAULT_CURRENCY) => {
  // Convert all prices to Dinero objects
  const prices = products.map((p) => money(p.price?.amount || 0, currency));

  // Calculate total price
  const total = prices.reduce((sum, price) => sum.add(price), money(0, currency));

  // Guard against division by zero: if total is zero, no discount can be allocated
  if (total.isZero()) {
    return products.map((product, i) => ({
      ...product,
      originalPrice: 0,
      discountedPrice: 0,
      discountAmount: 0,
    }));
  }

  // Create discount as Dinero object
  const discount = money(totalDiscountAmount, currency);

  // Calculate final price after discount
  let finalPrice = total.subtract(discount);

  // Guard against negative final price: if discount exceeds total, make everything free
  if (finalPrice.isNegative()) {
    finalPrice = money(0, currency);
  }

  // Calculate proportions (these are safe to use as floats for ratios)
  const ratios = prices.map((price) => price.getAmount() / total.getAmount());

  // Allocate the final price proportionally
  // Dinero.js handles remainder distribution automatically!
  const allocated = finalPrice.allocate(ratios);

  // Map back to products with discount information
  return products.map((product, i) => {
    const originalPrice = prices[i];
    const discountedPrice = allocated[i];
    const discountAmount = originalPrice.subtract(discountedPrice);

    return {
      // Include original product data first
      ...product,
      // Override with calculated values
      originalPrice: toAmount(originalPrice),
      discountedPrice: toAmount(discountedPrice),
      discountAmount: toAmount(discountAmount),
    };
  });
};

export default {
  money,
  toAmount,
  toPriceObject,
  allocateDiscount,
};
